import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useCallback, useMemo } from 'react';

import useControlledValue from '../../../../utils/hooks/use-controlled-value';

import type { UsePopupOptions } from './popup.types';

// eslint-disable-next-line ts/explicit-function-return-type
const usePopup = (props: UsePopupOptions) => {
  const { defaultOpen, onOpenChange, open, placement = 'bottom-start' } = props;

  const [isOpen, setIsOpen] = useControlledValue({
    controlledValue: open,
    defaultValue: defaultOpen,
    name: 'Popup',
    state: 'open',
  });

  const handleOnOpenChange = useCallback(
    (o: boolean, e?: Event): void => {
      setIsOpen(o);
      onOpenChange?.(o, e);
    },
    [setIsOpen, onOpenChange],
  );

  const { context, refs } = useFloating({
    middleware: [
      offset(4),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 4,
      }),
      shift({ padding: 4 }),
    ],
    onOpenChange: handleOnOpenChange,
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  const popupContextValue = useMemo(
    () => ({
      context,
      interactions,
      isOpen: !!isOpen,
      refs,
      setIsOpen: handleOnOpenChange,
    }),
    [isOpen, handleOnOpenChange, context, interactions, refs],
  );

  return popupContextValue;
};

export default usePopup;
